import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  LocationMarkerIcon, 
  MapIcon,
  AdjustmentsIcon,
  CheckCircleIcon
} from '@heroicons/react/outline';

const LocationPicker = ({ 
  onLocationSelect, 
  selectedLocation, 
  showRadius = false, 
  radius = 10, 
  onRadiusChange,
  placeholder = "Search for a location...",
  className = "",
  radiusLabel = "Service Radius"
}) => {
  const [location, setLocation] = useState(selectedLocation || null);
  const [currentRadius, setCurrentRadius] = useState(radius);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);

  const GOOGLE_PLACES_API_KEY = 'AIzaSyCzYmyVFNEcZKkVeUvM0d2OvT44rzN1AGk';

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMapsAPI = () => {
      if (window.google && window.google.maps) {
        console.log('🔍 Google Maps API already loaded');
        initializeMap();
        initializeAutocomplete();
        return;
      }

      console.log('🔍 Loading Google Maps API...');
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('🔍 Google Maps API loaded successfully');
        // Add a small delay to ensure the API is fully initialized
        setTimeout(() => {
          initializeMap();
          initializeAutocomplete();
        }, 100);
      };
      script.onerror = (error) => {
        console.error('🔍 Error loading Google Maps API:', error);
        setError('Failed to load map. Please refresh the page and try again.');
      };
      document.head.appendChild(script);
    };

    loadGoogleMapsAPI();
  }, []);

  const initializeMap = useCallback(() => {
    try {
      if (!window.google || !window.google.maps || !mapRef.current) {
        console.log('🔍 Cannot initialize map - missing dependencies');
        return;
      }

      console.log('🔍 Initializing Google Map...');
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 9.0820, lng: 7.3986 }, // Abuja, Nigeria
      zoom: 6,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

      console.log('🔍 Google Map initialized successfully');
    setMapLoaded(true);
    } catch (error) {
      console.error('🔍 Error initializing map:', error);
      setError('Failed to initialize map. Please refresh the page and try again.');
    }
  }, []);

  const initializeAutocomplete = useCallback(() => {
    try {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('🔍 Cannot initialize autocomplete - Google Maps Places not loaded');
        return;
      }

      const input = document.getElementById('location-input');
      if (!input) {
        console.log('🔍 Cannot initialize autocomplete - input element not found');
        return;
      }

      // Clear any existing autocomplete instance
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      console.log('🔍 Initializing Google Places Autocomplete...');
      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'ng' },
        fields: ['formatted_address', 'geometry', 'place_id'],
        types: ['geocode']
      });

      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
      console.log('🔍 Google Places Autocomplete initialized successfully');
    } catch (error) {
      console.error('🔍 Error initializing autocomplete:', error);
      setError('Failed to initialize location search. Please refresh the page and try again.');
    }
  }, []);

  const handlePlaceSelect = () => {
    console.log('🔍 handlePlaceSelect called');
    
    // Check if autocomplete is initialized
    if (!autocompleteRef.current) {
      console.error('🔍 Autocomplete not initialized');
      setError('Location search not ready. Please refresh the page and try again.');
      return;
    }
    
    const place = autocompleteRef.current.getPlace();
    console.log('🔍 Place object:', place);
    
    // Validate place object
    if (!place) {
      console.error('🔍 No place object returned');
      setError('No location selected. Please try again.');
      return;
    }
    
    if (!place.geometry) {
      console.error('🔍 Place has no geometry:', place);
      setError('No location details available for this place. Please select a different location.');
      return;
    }
    
    if (!place.geometry.location) {
      console.error('🔍 Place has no location:', place.geometry);
      setError('Location coordinates not available. Please select a different location.');
      return;
    }
    
    if (!place.formatted_address) {
      console.error('🔍 Place has no formatted address');
      setError('Location name not available. Please select a different location.');
      return;
    }

    const selectedLocationData = {
      name: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      placeId: place.place_id || null
    };
    
    console.log('🔍 Selected location data:', selectedLocationData);
    setLocation(selectedLocationData);
    setSearchQuery(place.formatted_address);
    setError('');
    
    if (onLocationSelect) {
      console.log('🔍 Calling onLocationSelect callback');
      onLocationSelect(selectedLocationData);
    }

    // Update map
    updateMap(selectedLocationData);
  };

  const updateMap = useCallback((locationData) => {
    if (!window.google || !window.google.maps || !mapInstanceRef.current) {
      return;
    }

    const position = { lat: locationData.latitude, lng: locationData.longitude };

    // Clear existing marker and circle
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }
    if (circleRef.current) {
      circleRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      title: locationData.name
    });

    // Add circle for radius (if showRadius is true)
    if (showRadius && currentRadius > 0) {
      try {
        circleRef.current = new window.google.maps.Circle({
          strokeColor: '#FF6B35',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: '#FF6B35',
          fillOpacity: 0.15,
          map: mapInstanceRef.current,
          center: position,
          radius: currentRadius * 1000 // Convert km to meters
        });
      } catch (error) {
        console.error('Error creating circle:', error);
      }
    }

    // Center map on location and adjust zoom based on radius
    mapInstanceRef.current.setCenter(position);
    const zoomLevel = showRadius ? Math.max(8, 15 - Math.log2(currentRadius)) : 12;
    mapInstanceRef.current.setZoom(zoomLevel);
  }, [showRadius, currentRadius]);

  // Update map when location or radius changes
  useEffect(() => {
    if (mapLoaded && location && showRadius) {
      updateMap(location);
    }
  }, [mapLoaded, location, currentRadius, showRadius, updateMap]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setError('');
  };

  const handleRadiusChange = useCallback((e) => {
    const newRadius = parseInt(e.target.value);
    setCurrentRadius(newRadius);
    
    if (onRadiusChange) {
      onRadiusChange(newRadius);
    }
    
    // Update circle on map
    if (location && mapInstanceRef.current) {
      if (circleRef.current) {
        circleRef.current.setRadius(newRadius * 1000);
      } else if (showRadius && newRadius > 0) {
        const position = { lat: location.latitude, lng: location.longitude };
        circleRef.current = new window.google.maps.Circle({
          strokeColor: '#FF6B35',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: '#FF6B35',
          fillOpacity: 0.15,
          map: mapInstanceRef.current,
          center: position,
          radius: newRadius * 1000
        });
      }
      
      // Adjust zoom level based on new radius
      const zoomLevel = Math.max(8, 15 - Math.log2(newRadius));
      mapInstanceRef.current.setZoom(zoomLevel);
    }
  }, [onRadiusChange, location, showRadius]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <LocationMarkerIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          id="location-input"
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              console.log('🔍 Enter key pressed in location input - preventing form submission');
            }
          }}
          placeholder={placeholder}
          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-CC5500 focus:border-transparent"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}

      {/* Map Visualization */}
      <div className="relative">
        <div
          ref={mapRef}
          className="w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center relative overflow-hidden"
        >
          {!mapLoaded ? (
            <div className="text-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-CC5500 mx-auto mb-2"></div>
              <p>Loading map...</p>
            </div>
          ) : !location ? (
            <div className="text-center text-gray-500">
              <MapIcon className="h-12 w-12 mx-auto mb-2" />
              <p className="font-medium">Select a location to see it on the map</p>
              <p className="text-sm text-gray-400 mt-1">This is required to complete your registration</p>
            </div>
          ) : (
            <div className="absolute inset-0">
              {/* Map will be rendered here by Google Maps API */}
            </div>
          )}
        </div>
      </div>

      {/* Selected Location Display */}
      {location && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <p className="font-medium text-green-800">
                {location.name}
              </p>
              <p className="text-sm text-green-600">
                Coordinates: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Radius Selector (only for suppliers) */}
      {showRadius && (
        <div className="space-y-3">
          <div className="flex items-center">
            <AdjustmentsIcon className="h-5 w-5 text-gray-400 mr-2" />
            <label className="text-sm font-medium text-gray-700">
              {radiusLabel}: {currentRadius} km
            </label>
          </div>
          
          <input
            type="range"
            min="5"
            max="100"
            step="5"
            value={currentRadius}
            onChange={handleRadiusChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          
          <div className="flex justify-between text-xs text-gray-500">
            <span>5 km</span>
            <span>25 km</span>
            <span>50 km</span>
            <span>100 km</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker; 